import React, { useEffect } from 'react';
import { ChevronRight, Phone, Truck, Globe, Package } from 'lucide-react';
const domesticRates = [{
  range: 'Orders to $25.00',
  rate: '$5.00'
}, {
  range: '$25.01 to $50.00',
  rate: '$6.50'
}, {
  range: '$50.01 to $75.00',
  rate: '$8.00'
}, {
  range: '$75.01 to $98.99',
  rate: '$9.95'
}, {
  range: '$99.00 and over',
  rate: 'FREE',
  highlight: true
}];
export function ShippingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <section className="bg-cream" data-id="element-1040">
      {/* Page Header */}
      <div className="bg-cream-dark border-b border-warmBorder" data-id="element-1041">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-id="element-1042">
          <nav className="flex items-center gap-2 text-sm text-warmGray mb-4" data-id="element-1043">
            <a href="#/" className="hover:text-gold transition-colors" data-id="element-1044">
              Home
            </a>
            <ChevronRight size={14} className="text-warmGray/50" data-id="element-1045" />
            <a href="#/support" className="hover:text-gold transition-colors" data-id="element-1046">
              Support
            </a>
            <ChevronRight size={14} className="text-warmGray/50" data-id="element-1047" />
            <span className="text-charcoal font-medium" data-id="element-1048">
              Shipping Information
            </span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-serif text-charcoal" data-id="element-1049">
            Shipping Information
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-id="element-1050">
        <div className="space-y-8" data-id="element-1051">
          {/* Intro */}
          <div className="bg-white p-8 md:p-10 rounded-sm shadow-sm border border-warmBorder text-center" data-id="element-1052">
            <div className="w-14 h-14 mx-auto bg-gold/10 rounded-full flex items-center justify-center mb-5" data-id="element-1053">
              <Truck className="w-7 h-7 text-gold" data-id="element-1054" />
            </div>
            <p className="text-warmGray text-lg leading-relaxed" data-id="element-1055">
              We ship via U.S. Postal Service (USPS).
            </p>
          </div>

          {/* U.S. Postal Service */}
          <div className="bg-white p-8 md:p-10 rounded-sm shadow-sm border border-warmBorder" data-id="element-1056">
            <div className="flex items-center gap-3 mb-6" data-id="element-1057">
              <Package className="w-6 h-6 text-gold" data-id="element-1058" />
              <h2 className="text-2xl font-serif text-charcoal" data-id="element-1059">
                U.S. Postal Service
              </h2>
            </div>

            <p className="text-warmGray leading-relaxed mb-6" data-id="element-1060">
              In the continental United States: Shipping fees are shown in the
              following chart. Orders totalling $100.00 and over will have FREE
              SHIPPING. The shipping charges will be removed at time of
              shipment. Credit cards are not charged until an order is ready to
              ship.
            </p>

            {/* Rate Table */}
            <div className="border border-warmBorder rounded-sm overflow-hidden mb-8" data-id="element-1061">
              <table className="w-full" data-id="element-1062">
                <thead data-id="element-1063">
                  <tr className="bg-cream-dark" data-id="element-1064">
                    <th className="text-left px-6 py-3 text-sm font-bold text-charcoal uppercase tracking-wider" data-id="element-1065">
                      Order Total
                    </th>
                    <th className="text-right px-6 py-3 text-sm font-bold text-charcoal uppercase tracking-wider" data-id="element-1066">
                      Shipping Cost
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1067">
                  {domesticRates.map((row, idx) => <tr key={idx} className={`border-t border-warmBorder ${row.highlight ? 'bg-gold/5' : idx % 2 === 0 ? 'bg-white' : 'bg-cream/30'}`} data-id="element-1068">
                      <td className="px-6 py-3.5 text-warmGray" data-id="element-1069">{row.range}</td>
                      <td className={`px-6 py-3.5 text-right ${row.highlight ? 'text-gold font-bold text-lg' : 'text-charcoal font-medium'}`} data-id="element-1070">
                        {row.rate}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Alaska, Hawaii, Territories */}
            <div className="bg-cream-dark p-6 rounded-sm border border-warmBorder" data-id="element-1071">
              <p className="text-warmGray leading-relaxed text-sm" data-id="element-1072">
                <span className="font-bold text-charcoal" data-id="element-1073">
                  Alaska, Hawaii and U.S. Territories:
                </span>{' '}
                All orders to these areas are shipped via USPS at a rate of
                $6.00 for first item and $1.00 for each additional item.
              </p>
              <p className="text-warmGray leading-relaxed text-sm mt-3" data-id="element-1074">
                <span className="font-bold text-charcoal" data-id="element-1075">Canada:</span> All
                orders to Canada are shipped via USPS at a rate of $19.00 U.S.
                and $1.00 U.S. for each additional item. Please see
                International Shipping below for more information regarding
                duties and taxes.
              </p>
            </div>
          </div>

          {/* International Shipping */}
          <div className="bg-white p-8 md:p-10 rounded-sm shadow-sm border border-warmBorder" data-id="element-1076">
            <div className="flex items-center gap-3 mb-6" data-id="element-1077">
              <Globe className="w-6 h-6 text-gold" data-id="element-1078" />
              <h2 className="text-2xl font-serif text-charcoal" data-id="element-1079">
                International Shipping Information
              </h2>
            </div>

            <p className="text-warmGray leading-relaxed mb-5" data-id="element-1080">
              International shipping is restricted to Canada Only.
            </p>

            <p className="text-warmGray leading-relaxed mb-5" data-id="element-1081">
              The <span className="font-bold text-charcoal" data-id="element-1082">Free Shipping</span>{' '}
              option is not available on International Orders. During checkout,
              for orders over $100.00, your invoice will show Free Shipping,
              however, when your order is shipped you will be charged for
              shipping using the rates below. Canadian shipments are sent via
              USPS First Class Package International at a rate of $19.00 U.S.
              per order plus 1.00 U.S. for each item included in shipment.
            </p>

            <p className="text-warmGray leading-relaxed mb-5" data-id="element-1083">
              All Canadian fees such as taxes and duties are established by
              Canadian Customs and not the responsibility of Switchplate
              Gallery. Items marked as "Gifts" may be subject to the above fees
              upon reaching a certain value. PST and GST taxes may vary by
              province. Please check with you local Customs authorities for
              local rates.
            </p>
          </div>

          {/* Contact CTA */}
          <div className="bg-charcoal text-cream p-8 md:p-10 rounded-sm" data-id="element-1084">
            <h3 className="text-xl font-serif text-white mb-3" data-id="element-1085">
              For More Information
            </h3>
            <p className="text-cream/80 leading-relaxed mb-6" data-id="element-1086">
              Have questions about shipping? Give us a call and we'll be happy
              to help.
            </p>
            <a href="tel:13046863170" className="inline-flex items-center gap-2 px-8 py-3.5 bg-gold hover:bg-gold-light text-white font-bold rounded-sm transition-colors text-sm" data-id="element-1087">
              <Phone size={16} data-id="element-1088" />
              1-304-686-3170
            </a>
            <p className="text-cream/60 text-sm mt-4" data-id="element-1089">
              Monday–Friday, 10AM–5PM EST
            </p>
          </div>
        </div>
      </div>
    </section>;
}